// ═══════════════════════════════════════════════════════════════════════════
// Basic block AI → EditorCommand translators (CREATE_BLOCK / INSERT /
// EXPLODE / UPDATE_ATTRIBUTE).
// ═══════════════════════════════════════════════════════════════════════════

import type { EditorCommand } from "@dxf-ai-editor/contracts";

import type {
  CreateBlockAiCommand,
  ExplodeAiCommand,
  InsertBlockAiCommand,
  UpdateAttributeAiCommand,
} from "./blockSchemas";
import {
  AI_COMMAND_SOURCE,
  AI_INSERT_GROUP_SIZE_HINT,
  LAST_INSERT_ID_SENTINEL,
  degreesToRadians,
  pickSpaceId,
  type AiDispatchContext,
} from "./blockDispatcherShared";

// ─── CREATE_BLOCK ─────────────────────────────────────────────────
export function translateCreateBlock(
  command: CreateBlockAiCommand,
): EditorCommand[] {
  const cmd: EditorCommand = {
    type: "geometry/create-block-definition",
    source: AI_COMMAND_SOURCE,
    blockDef: {
      name: command.name,
      handle: "",
      layer: "0",
      flags: 0,
      basePoint: {
        x: command.basePoint.x,
        y: command.basePoint.y,
        z: 0,
      },
      xrefPathName: "",
      description: command.description,
      entities: command.entities,
    },
    blockRecord: {
      name: command.name,
      insertionUnits: command.insertionUnits,
      explodable: command.explodable,
      scalable: command.scalable,
      isAnnotative: command.isAnnotative,
      description: command.description,
    },
    postAction: command.postAction,
    sourceEntityIds: command.sourceEntityIds,
    ...(command.convertSpaceId !== undefined
      ? { convertSpaceId: command.convertSpaceId }
      : {}),
    ...(command.convertInsertPoint !== undefined
      ? {
          convertInsertPoint: {
            x: command.convertInsertPoint.x,
            y: command.convertInsertPoint.y,
          },
        }
      : {}),
  };
  return [cmd];
}

// ─── INSERT_BLOCK ─────────────────────────────────────────────────
//
// AutoCAD MINSERT expands to rows*columns individual INSERTs. Our model
// currently has one INSERT entity per placement, so we fan out here. For
// a non-array INSERT (columnCount === rowCount === 1) we emit a single
// command — backwards compatible with the simplest case.
export function translateInsertBlock(
  command: InsertBlockAiCommand,
  context: AiDispatchContext,
): EditorCommand[] {
  const spaceId = pickSpaceId(command, context);
  const rotationRad = degreesToRadians(command.rotation);
  const commands: EditorCommand[] = [];
  // Number of `create-block-insert` commands this INSERT_BLOCK fans out to —
  // stamped on the trailing sentinel so the dispatch boundary can bind the
  // attribute update to the FIRST insert of THIS block's group (#283-1).
  const groupSize = command.rowCount * command.columnCount;
  for (let row = 0; row < command.rowCount; row++) {
    for (let col = 0; col < command.columnCount; col++) {
      const x = command.insertionPoint.x + col * command.columnSpacing;
      const y = command.insertionPoint.y + row * command.rowSpacing;
      commands.push({
        type: "geometry/create-block-insert",
        source: AI_COMMAND_SOURCE,
        spaceId,
        blockName: command.blockName,
        insertionPoint: { x, y },
        scaleX: command.scaleX,
        scaleY: command.scaleY,
        rotation: rotationRad,
      });
    }
  }

  // Attribute assignment — apply the AI-supplied values to the first
  // MINSERT copy. Multi-instance ATTRIB customisation for MINSERT is
  // not supported in AutoCAD either (all instances share values).
  // The dispatcher reports the insert id via a subsequent
  // `geometry/update-attribute-values` with the `LAST_INSERT_ID_SENTINEL`
  // placeholder so the safe-contour boundary can substitute the concrete id of
  // the INSERT it just created BEFORE this command is dispatched (see
  // `aiSafeContour.ts` → `dispatchAiCommandSequence`). If the caller cannot
  // resolve the sentinel it DROPS the command — the reducer is a graceful
  // no-op on the unknown sentinel id, so attribute values are never written
  // against a non-existent id.
  if (command.attributes.length > 0) {
    commands.push({
      type: "geometry/update-attribute-values",
      source: AI_COMMAND_SOURCE,
      insertId: LAST_INSERT_ID_SENTINEL,
      updates: command.attributes.map((a) => ({
        tag: a.tag,
        newValue: a.value,
      })),
      // Internal hint (stripped by the boundary before dispatch — see
      // AI_INSERT_GROUP_SIZE_HINT): tells the contour how many creates belong to
      // THIS block so an adjacent attr-free + attr INSERT_BLOCK pair each bind
      // their attrs to their OWN first insert (#283-1).
      [AI_INSERT_GROUP_SIZE_HINT]: groupSize,
    } as EditorCommand);
  }

  return commands;
}

// ─── EXPLODE ──────────────────────────────────────────────────────
export function translateExplode(command: ExplodeAiCommand): EditorCommand[] {
  return command.insertIds.map<EditorCommand>((insertId) => ({
    type: "geometry/explode-insert",
    source: AI_COMMAND_SOURCE,
    insertId,
    resolveNested: command.resolveNested,
  }));
}

// ─── UPDATE_ATTRIBUTE ─────────────────────────────────────────────
export function translateUpdateAttribute(
  command: UpdateAttributeAiCommand,
): EditorCommand[] {
  const cmd: EditorCommand = {
    type: "geometry/update-attribute-value",
    source: AI_COMMAND_SOURCE,
    insertId: command.insertId,
    tag: command.tag,
    newValue: command.newValue,
    ...(command.overrides !== undefined
      ? {
          overrides: {
            ...(command.overrides.overrideHeight !== undefined
              ? { overrideHeight: command.overrides.overrideHeight }
              : {}),
            ...(command.overrides.overrideRotation !== undefined
              ? { overrideRotation: command.overrides.overrideRotation }
              : {}),
            ...(command.overrides.overridePosition !== undefined
              ? {
                  overridePosition: {
                    x: command.overrides.overridePosition.x,
                    y: command.overrides.overridePosition.y,
                  },
                }
              : {}),
            ...(command.overrides.overrideStyle !== undefined
              ? { overrideStyle: command.overrides.overrideStyle }
              : {}),
            ...(command.overrides.invisible !== undefined
              ? { invisible: command.overrides.invisible }
              : {}),
          },
        }
      : {}),
  };
  return [cmd];
}
